import { useEffect, useRef, useState } from 'react';
import { Button } from '~/components';
import { LoadingBar } from './LoadingBar';

interface Props {
  children: React.ReactNode;
}

export const Layout = ({ children }: Props) => {
  return <div className="grid grid-rows-[auto_1fr_auto] w-full h-full gap-8">{children}</div>;
};

export const Container = ({ children }: Props) => {
  return <div className="grid grid-rows-[auto_1fr_auto] w-full h-full gap-4">{children}</div>;
};

interface HeaderProps {
  title: string;
  subtitle: string;
  cyTag?: string;
}
export const Header = ({ title, subtitle, cyTag }: HeaderProps) => {
  return (
    <div data-cy={cyTag} className="flex flex-col gap-2 w-full">
      <h1 className="text-left font-bold text-[30px] leading-9 sm:text-[43px] sm:leading-10">
        {title}
      </h1>
      <p className="text-left">{subtitle}</p>
    </div>
  );
};

// Scrollable form body with a bottom fade-gradient cue that only appears when
// content overflows and the user is not yet at the bottom. Absent the cue,
// users mistake the viewport height for the whole form and miss fields below.
//
// [MCV-1280] An earlier implementation combined ResizeObserver, a scroll
// listener, and a MutationObserver({childList, subtree}) on the scroll
// container. The MutationObserver fired on every React reconciler mutation
// under the subtree — dozens per keystroke across all fields — each time
// forcing a synchronous layout read. Replaced with a single
// IntersectionObserver on a zero-height sentinel at the bottom of the
// content: IO natively covers scroll, root resize, and content growth (which
// moves the sentinel) without observing React's DOM churn.
interface FormContentProps extends Props {
  // Set false for steps whose page shell already scrolls (CV upload), so phones
  // do not get a scroller inside a scroller that traps touch scrolling. The
  // 465px cap and inner scroll then apply only from `sm` up.
  scrollOnPhones?: boolean;
}

export const FormContent = ({ children, scrollOnPhones = true }: FormContentProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    // rootMargin bottom=4px extends the intersection rect below the viewport
    // by 4px, so a sentinel within 4px of the visible bottom still counts as
    // intersecting — preserves the original `distanceFromBottom > 4` jitter
    // tolerance without arithmetic.
    const observer = new IntersectionObserver(([entry]) => setHasMoreBelow(!entry.isIntersecting), {
      root,
      threshold: 0,
      rootMargin: '0px 0px 4px 0px',
    });
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative min-h-0 h-full">
      <div
        ref={scrollRef}
        className={
          scrollOnPhones
            ? 'h-full max-h-[465px] overflow-y-auto px-2'
            : 'h-full px-2 sm:max-h-[465px] sm:overflow-y-auto'
        }
        data-has-more-below={hasMoreBelow || undefined}
      >
        {children}
        <div ref={sentinelRef} aria-hidden="true" />
      </div>
      <div
        aria-hidden
        className={`pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent transition-opacity duration-150 ${
          hasMoreBelow ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
};

interface NextConfig {
  // Provide `formId` to submit a <form id={formId} /> rendered elsewhere in
  // the tree — browser-native form association. Provide `onClick` for
  // non-form actions (skip, complete). Exactly one should be set.
  formId?: string;
  onClick?: () => void;
  label?: string;
  cyTag?: string;
  disabled?: boolean;
}

interface FooterProps {
  handleBack: () => void;
  loadingPercent?: number;
  next?: NextConfig;
  cyTags?: {
    back: string;
    loadingBar: string;
  };
}

// Action bar for registration pages. Renders below <FormContent> so the
// primary action is always visible without scrolling the form body.
export const Footer = ({ handleBack, loadingPercent, next, cyTags }: FooterProps) => {
  return (
    <div className="flex flex-col gap-3 w-full mt-5">
      <div className="flex items-center justify-between gap-4 w-full">
        <button
          type="button"
          data-cy={cyTags?.back}
          onClick={handleBack}
          aria-label="Back"
          className="cursor-pointer text-grey-300 text-sm font-semibold leading-5"
        >
          {'< Back'}
        </button>
        {next &&
          (next.onClick !== undefined ? (
            <Button
              cyTag={next.cyTag}
              colorVariant="translucent"
              label={next.label || 'Next'}
              disabled={next.disabled}
              type="button"
              onClick={next.onClick}
            />
          ) : (
            <Button
              cyTag={next.cyTag}
              colorVariant="translucent"
              label={next.label || 'Next'}
              disabled={next.disabled}
              type="submit"
              form={next.formId}
            />
          ))}
      </div>
      {loadingPercent !== undefined && (
        <div className="flex flex-row justify-end items-center">
          <LoadingBar cyTag={cyTags?.loadingBar} percent={loadingPercent} height={3.5} />
        </div>
      )}
    </div>
  );
};

// Retained for any caller still rendering the button inline. New code should
// pass `next` to <Footer /> so the button lives in the page's action row
// rather than inside the scrollable form body.
interface NextProps {
  handleNext?: () => void;
  label?: string;
  cyTag?: string;
  formId?: string;
}
export const NextButton = ({ handleNext, label, cyTag, formId }: NextProps) => {
  const label_ = label || 'Next';
  return (
    <div className="flex flex-1 justify-end items-end">
      {handleNext ? (
        <Button
          cyTag={cyTag}
          colorVariant="translucent"
          label={label_}
          type="button"
          onClick={handleNext}
        />
      ) : (
        <Button
          cyTag={cyTag}
          colorVariant="translucent"
          label={label_}
          type="submit"
          form={formId}
        />
      )}
    </div>
  );
};
